import { Divider, Tooltip } from '@affine/component';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkbenchService } from '@affine/core/modules/workbench';
import {
  type WorkspaceMetadata,
  WorkspaceService,
  WorkspacesService,
} from '@affine/core/modules/workspace';
import { Permission } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import { memo, useCallback, useMemo } from 'react';

import { useNavigateHelper } from '../../hooks/use-navigate-helper';
import { WorkspaceAvatar } from '../../workspace-avatar';
import * as styles from './index.css';

type WorkspaceProfile = ReturnType<WorkspacesService['getProfile']>;
interface WorkspaceInfo {
  profile: WorkspaceProfile;
  meta: WorkspaceMetadata;
  badgeText: string;
}

interface TeamItemProps {
  workspaces: WorkspaceInfo[];
}

const TeamItem = memo(({ workspaces }: TeamItemProps) => {
  const t = useI18n();

  const { jumpToPage } = useNavigateHelper();
  const workbench = useServiceOptional(WorkbenchService)?.workbench;

  const handleJumpToWorkspace = useCallback(() => {
    const closeInactiveViews = () =>
      workbench?.views$.value.forEach(view => {
        if (workbench?.activeView$.value !== view) {
          workbench?.close(view);
        }
      });
    if (workspaces.length !== 1) {
      return;
    }

    if (document.startViewTransition) {
      document.startViewTransition(() => {
        closeInactiveViews();
        jumpToPage(workspaces[0].profile.id, 'all');
        return new Promise(resolve =>
          setTimeout(resolve, 150)
        ); /* start transition after 150ms */
      });
    } else {
      closeInactiveViews();
      jumpToPage(workspaces[0].profile.id, 'all');
    }
  }, [jumpToPage, workbench, workspaces]);

  const handleClick = useCallback(() => {
    if (workspaces.length === 1) {
      handleJumpToWorkspace();
    } else {
      workbench?.setWorkspaceSelectorOpen(true);
    }
  }, [handleJumpToWorkspace, workbench, workspaces.length]);

  if (workspaces.length === 0) return null;

  const displayName =
    workspaces.length > 1
      ? t['com.streamlain.workspace.cloud.account.team.multi']()
      : workspaces[0].profile.profile$.value?.name || 'Team';

  const tooltipContent =
    workspaces.length > 1
      ? t['com.streamlain.workspace.cloud.account.team.tips-2']()
      : t['com.streamlain.workspace.cloud.account.team.tips-1']();

  return (
    <Tooltip content={tooltipContent}>
      <div
        className={styles.teamWorkspace}
        data-testid="user-info-workspace-row"
        onClick={handleClick}
      >
        <div className={styles.teamAvatarStack}>
          {workspaces.map((workspace, index) => (
            <WorkspaceAvatar
              key={workspace.profile.id}
              meta={workspace.meta}
              rounded={3}
              size={16}
              name={workspace.profile.profile$.value?.name}
              colorfulFallback
              className={clsx(styles.workspaceAvatar, {
                ['multi-avatar']: index !== 0,
              })}
            />
          ))}
        </div>
        <span className={styles.teamName}>{displayName}</span>
        <span
          className={styles.teamBadge}
          data-testid="user-info-workspace-role"
        >
          {workspaces[0].badgeText}
        </span>
      </div>
    </Tooltip>
  );
});

TeamItem.displayName = 'TeamItem';

const workspaceRoleLabel = (
  t: ReturnType<typeof useI18n>,
  role: Permission | null | undefined
) => {
  switch (role) {
    case Permission.Owner:
      return t['com.streamlain.settings.workspace.permissions.role.owner']();
    case Permission.Admin:
      return t['com.streamlain.settings.workspace.permissions.role.admin']();
    case Permission.Editor:
      return t['com.streamlain.settings.workspace.permissions.role.editor']();
    case Permission.Reader:
      return t['com.streamlain.settings.workspace.permissions.role.reader']();
    default:
      return t['com.streamlain.workspace.cloud.account.role.loading']();
  }
};

const withGuestSuffix = (
  t: ReturnType<typeof useI18n>,
  roleLabel: string,
  isHouseGuest: boolean | undefined
) => {
  if (!isHouseGuest) return roleLabel;
  return `${roleLabel} · ${t['com.streamlain.share-menu.member-management.guest-badge']()}`;
};

const resolveLocalWorkspaceRole = (
  profile: WorkspaceProfile,
  meta: WorkspaceMetadata,
  currentRole: Permission | null | undefined,
  isCurrent: boolean
): Permission | null | undefined => {
  return (
    (isCurrent ? currentRole : null) ??
    profile.profile$.value?.role ??
    meta.role
  );
};

export const TeamList = memo(() => {
  const t = useI18n();
  const workspacesService = useService(WorkspacesService);
  const currentWorkspaceId = useServiceOptional(WorkspaceService)?.workspace.id;
  const permissionService = useServiceOptional(WorkspacePermissionService);
  const currentRole = useLiveData(permissionService?.permission.role$);
  const workspaceMetas = useLiveData(workspacesService.list.workspaces$);
  const workspaceProfiles = workspacesService.getAllWorkspaceProfile();

  const teamWorkspaces = useMemo(() => {
    const byBadge = new Map<string, WorkspaceInfo[]>();

    const metaMap = new Map(workspaceMetas.map(meta => [meta.id, meta]));

    for (const profile of workspaceProfiles) {
      const meta = metaMap.get(profile.id);
      if (!meta) continue;
      if (!(meta.team ?? profile.profile$.value?.isTeam)) continue;

      const isCurrent = profile.id === currentWorkspaceId;
      const role = resolveLocalWorkspaceRole(
        profile,
        meta,
        currentRole,
        isCurrent
      );
      const badgeText = withGuestSuffix(
        t,
        workspaceRoleLabel(t, role),
        profile.profile$.value?.membershipKind === 'house_guests'
      );
      const group = byBadge.get(badgeText) ?? [];
      group.push({ profile, meta, badgeText });
      byBadge.set(badgeText, group);
    }

    return Array.from(byBadge.values()).map(workspaces =>
      // only show avatar stack when multiple workspaces share the same role.
      workspaces.slice(0, 3)
    );
  }, [currentRole, currentWorkspaceId, t, workspaceProfiles, workspaceMetas]);

  if (teamWorkspaces.length === 0) {
    return null;
  }

  return (
    <>
      {teamWorkspaces.map(workspaces => (
        <TeamItem key={workspaces[0].badgeText} workspaces={workspaces} />
      ))}
      <Divider />
    </>
  );
});

TeamList.displayName = 'TeamList';
